import { createContext } from '@fluentui/react-context-selector';
import type { AppTranslations } from 'translations/translations';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import type { AccessPermissionType } from '../ExtensionsPage/accessPermissions';

export type ExtensionType = 'docker-zip' | 'docker-image';

type ExtensionEnvironmentVariable = {
  key: string;
  value: string;
};

export type Extension = {
  id: string;
  name: string;
  slug: string;
  type: ExtensionType;
  description: string | null;
  environmentVariables: ExtensionEnvironmentVariable[];
  status: string;
  resources: {
    cpu: string | null;
    memory: string | null;
  };
  accessPermission: AccessPermissionType;
  accessibleByUsers: string[];
};

export type ExtensionUser = {
  id: string;
  username: string;
};

export interface ExtensionsState {
  addExtensionDialogOpen: boolean;
  editExtensionDialogOpen: boolean;
  readonlyExtensionDialogOpen: boolean;
  extensions: Extension[];
  extensionsLoading: boolean;

  extensionDetails: {
    extension: Extension;
    logs: string[];
    mode: 'edit' | 'readonly';
    logsRefreshing: boolean;
  } | null;

  platformUsers: ExtensionUser[];
}

export interface ExtensionsAdditionalCommandContext {
  api: ApiHook;
  msg: AppTranslations;
  showNotification: (command: ShowNotificationCommand) => void;
}

export interface ExtensionsOuterState {
  msg: AppTranslations;
}

type State = ExtensionsState;
type CommandContext = ExtensionsAdditionalCommandContext;
type OuterState = ExtensionsOuterState;

export type ExtensionsCommandContext = CommandModelCommandContext<
  State,
  CommandContext,
  OuterState
>;
export type ExtensionsCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<State, CommandContext, OuterState, TCommandArgs>;
export type ExtensionsDispatch = CommandModelCommandDispatcher<
  State,
  CommandContext,
  OuterState
>;
export type ExtensionsMergedState = CommandModelMergedState<State, OuterState>;
export type ExtensionsModel = CommandModel<State, CommandContext, OuterState>;

export const createInitialExtensionsState = (): ExtensionsState => ({
  addExtensionDialogOpen: false,
  readonlyExtensionDialogOpen: false,
  editExtensionDialogOpen: false,
  extensions: [],
  extensionsLoading: false,
  extensionDetails: null,
  platformUsers: [],
});

export const ExtensionsContext = createContext<ExtensionsModel>(
  {} as ExtensionsModel,
);

export const useExtensionsContextSelectors: ContextSelectorsHook<
  ExtensionsModel
> = (selectors) => useContextSelectors(ExtensionsContext, selectors);
